import React, { useState } from "react";
import { FileRejection } from "react-dropzone/typings/react-dropzone";
import { Message, Progress } from "semantic-ui-react";
import { ExtractFilesUploadInstruction, ExtractFileUploadInstruction, MultipleFilesApi, SingleFileApi, uploadFilesToCoreApi, uploadFileToCoreApi } from "utilities/fileUploadUtils";
import { type FileTypes } from "utilities/fileutils";
import { useElementHeight } from "utilities/hookutils";
import { FileDropzone } from "./FileDropzone";
import { getAcceptForFileType, getHtmlAcceptForFileType, getIconTypeForFileType, UploadProgress } from "./utils";
import { AxiosResponse } from "node_modules/axios";

type FileUploadWithValueProps = {
  maxHeight: number;
  currentValue: JSX.Element | null;
  maxSizeBytes: number;
  fileType: Exclude<FileTypes, "unknown">;
  endpoint: string,

}
  & (
    {
      multipleFiles: true;
      api: MultipleFilesApi;
      instruction: Omit<ExtractFilesUploadInstruction<MultipleFilesApi>, "files">;
      endpoint: string,
      onUploadComplete: () => void;
    } | {
      multipleFiles: false;
      api: SingleFileApi;
      endpoint: string,
      instruction: Omit<ExtractFileUploadInstruction<SingleFileApi>, "file">;
      onUploadComplete: (fileId: string, downloadUrl: string) => void;
    }
  )

// TODO: This should work for other file types as well
const FileUploadWithValue = (props: FileUploadWithValueProps) => {
  const fileUploadRef = React.useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [progress, setProgress] = useState<UploadProgress>({
    totalSize: 0,
    uploaded: 0,
  });

  const displayDropzone = props.currentValue === null;
  const heightHelper = useElementHeight();

  const onFileUploadInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    onDrop(files);
  }

  const onValueClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.preventDefault();
    if (fileUploadRef.current) {
      fileUploadRef.current.click();
    }
  }

  const onDrop = async (files: FileList | File[]) => {
    setError(undefined);
    if (!props.multipleFiles && files.length !== 1) {
      return;
    }

    const onUploadProgress = (e: ProgressEvent<EventTarget>) => {
      setProgress({
        totalSize: e.total,
        uploaded: e.loaded
      });
    };

    setIsUploading(true);

    let res: AxiosResponse<server.dto.ApiResponseWrapper<server.dto.FileUploadResult | object>>;
    if (props.multipleFiles === true) {
      res = await uploadFilesToCoreApi(
        props.api,
        props.endpoint,
        {
          ...props.instruction,
          files: Array.from(files)
        },
        {
          onUploadProgress,
          fileType: props.fileType,
          timeout: 60000, // 1 minute
          overrideMaxFileLength: props.maxSizeBytes
        }
      ) as AxiosResponse<server.dto.ApiResponseWrapper<server.dto.FileUploadResult>>;
    } else {
      res = await uploadFileToCoreApi(
        props.api,
        props.endpoint,
        {
          ...props.instruction,
          file: files[0]
        },
        {
          onUploadProgress,
          fileType: props.fileType,
          timeout: 60000, // 1 minute
          overrideMaxFileLength: props.maxSizeBytes
        }
      );
    }
    if (res.data.hasErrors) {
      setError(res.data.errors[0].message);
      setIsUploading(false);
      return;
    }
    setIsUploading(false);
    if (res.data.data != null && "fileId" in res.data.data && !props.multipleFiles) {
      props.onUploadComplete(res.data.data.fileId, res.data.data.downloadUrl);
    } else if (props.multipleFiles) {
      props.onUploadComplete();
    }

  }

  const onDropReject = (fileRejections: FileRejection[]) => {
    const firstRejectionError = fileRejections[0].errors[0].message;
    setError(firstRejectionError);
  }

  return <div style={{
    minHeight: heightHelper.height,
    height: "100%",
  }} ref={heightHelper.ref} className="grow1 flex-column gap-small">
    {(!displayDropzone && !isUploading) && <>
      <input
        ref={fileUploadRef}
        onChange={onFileUploadInputChange}
        type="file"
        multiple={props.multipleFiles}
        accept={getHtmlAcceptForFileType(props.fileType)}
        hidden
      />
      <div onClick={onValueClick} style={{ maxHeight: props.maxHeight, height: "100%" }} className="file-upload-image-wrapper">
        {props.currentValue}
      </div>
    </>}
    {(displayDropzone && !isUploading) && <FileDropzone
      iconType={getIconTypeForFileType(props.fileType)}
      accept={getAcceptForFileType(props.fileType)}
      maxFiles={props.multipleFiles ? 10 : 1}
      maxSizeBytes={props.maxSizeBytes}
      onDrop={onDrop}
      onDropReject={onDropReject}
    />}
    {isUploading && <div style={{ width: "100%" }}>
      <Progress
        percent={progress.uploaded / progress.totalSize * 100}
        color="green"
        progress
        label={<div style={{ color: "var(--text-color)" }}>{RESX.File.UploadingFiles}</div>}
        active={true} />
    </div>}
    {error && <div>
      <Message color="red">{error}</Message>
    </div>}
  </div>
}

interface BrandFileUploadProps {
  brandId: number;
  url: string | null;
  onUploadComplete: (fileId: string, downloadUrl: string) => void;
}

export const BrandLogoFileUpload = (props: BrandFileUploadProps) => {
  return <FileUploadWithValue
    multipleFiles={false}
    maxHeight={2000}
    fileType="image"
    maxSizeBytes={serverReference.constants.file.maximumImageFileContentLength}
    currentValue={props.url ? <img
      style={{ maxWidth: "100%", maxHeight: "100%" }}
      src={props.url}
    /> : null}
    api={window.CoreApi.Brand.SetLogo}
    endpoint={window.CoreApi.Brand.SetLogo.absoluteUrl()}
    instruction={{
      brandId: props.brandId,
    }}
    onUploadComplete={props.onUploadComplete}
  />
}

export const BrandIconFileUpload = (props: BrandFileUploadProps) => {
  return <FileUploadWithValue
    multipleFiles={false}
    maxHeight={2000}
    fileType="image"
    maxSizeBytes={serverReference.constants.file.maximumImageFileContentLength}
    currentValue={props.url ? <img
      style={{ maxWidth: "100%", maxHeight: "100%" }}
      src={props.url}
    /> : null}
    api={window.CoreApi.Brand.SetIcon}
    endpoint={window.CoreApi.Brand.SetIcon.absoluteUrl()}
    instruction={{
      brandId: props.brandId,
    }}
    onUploadComplete={props.onUploadComplete}
  />
}

interface BookkeepingLogoFileUploadProps {
  bookkeepingId: number;
  logo: server.dto.BookkeepingLogoFile | null;
  onUploadComplete: (fileId: string, downloadUrl: string) => void;
}

export const BookkeepingLogoFileUpload = (props: BookkeepingLogoFileUploadProps) => {
  return <FileUploadWithValue
    multipleFiles={false}
    maxHeight={180}
    fileType="image"
    maxSizeBytes={serverReference.constants.file.maximumImageFileContentLength}
    currentValue={props.logo ? <img
      style={{ maxWidth: "100%", maxHeight: "100%" }}
      src={props.logo.downloadUrl}
    /> : null}
    api={window.CoreApi.Bookkeeping.SetLogo}
    endpoint={window.CoreApi.Bookkeeping.SetLogo.absoluteUrl()}
    instruction={{
      bookkeepingId: props.bookkeepingId,
    }}
    onUploadComplete={props.onUploadComplete}
  />
}

interface FlightPassengerLogoFileUploadProps {
  flightPassengerId: number;
  onUploadComplete: () => void;
}
export const FlightPassengerFileUpload = (props: FlightPassengerLogoFileUploadProps) => {
  return <FileUploadWithValue
    multipleFiles
    maxHeight={180}
    fileType="any"
    maxSizeBytes={serverReference.constants.file.maximumTravelFileContentLength}
    currentValue={null}
    api={window.CoreApi.Flight.UploadPassengerFiles}
    endpoint={window.CoreApi.Flight.UploadPassengerFiles.absoluteUrl()}
    instruction={{
      flightPassengerId: props.flightPassengerId,
    }}
    onUploadComplete={props.onUploadComplete}
  />
}

interface TrainFileUploadProps {
  trainId: number;
  onUploadComplete: () => void;
}

export const TrainFileUpload = (props: TrainFileUploadProps) => {
  return <FileUploadWithValue
    multipleFiles
    maxHeight={180}
    fileType="any"
    maxSizeBytes={serverReference.constants.file.maximumTravelFileContentLength}
    currentValue={null}
    api={window.CoreApi.Train.UploadFiles}
    endpoint={window.CoreApi.Train.UploadFiles.absoluteUrl()}
    instruction={{
      trainId: props.trainId,
    }}
    onUploadComplete={props.onUploadComplete}
  />
}